import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import ImageUploader from "./ImageUploader";

export const SHOP_CATEGORIES = {
  Perfumes: { category: "Perfumes", subcategory: "Eau de Parfum", types: ["Eau de Parfum", "Eau de Toilette", "Extrait de Parfum", "Perfume Oil", "Body Mist"] },
  "Air Fresheners": { category: "Home Fragrance", subcategory: "Air Fresheners" },
  Candles: { category: "Home Fragrance", subcategory: "Candles" },
  Skincare: { category: "Skincare", subcategory: "Serums", types: ["Serums", "Body Oils", "Creams & Butters", "Cleansers", "Masks"] },
  Soaps: { category: "Soaps", subcategory: "Bar Soap", types: ["Bar Soap", "Liquid Soap", "Body Wash"] },
};

export const shopCategoryOf = (p) =>
  p.subcategory === "Air Fresheners" ? "Air Fresheners" : p.subcategory === "Candles" ? "Candles"
    : p.category === "Skincare" ? "Skincare" : p.category === "Soaps" ? "Soaps" : "Perfumes";

const EMPTY = {
  name: "", brand: "Queen MJ Maison Beaute", category: "Perfumes", subcategory: "Eau de Parfum", gender: "Unisex",
  fragrance_family: "", price: 0, description: "", notes: { top: [], heart: [], base: [] }, images: [], stock: 0,
  is_best_seller: false, is_new_arrival: false, rating: 4.5, reviews_count: 0, intensity: "Moderate",
  volumes: [{ size: "50ml", price_delta: 0 }],
};

const volumesToText = (v) => (v || []).map((x) => `${x.size}:${x.price_delta || 0}`).join(", ");
const textToVolumes = (t) => t.split(",").map((s) => s.trim()).filter(Boolean).map((s) => {
  const [size, delta] = s.split(":");
  return { size: size.trim(), price_delta: Number(delta) || 0 };
});

export default function ProductModal({ product, onClose, onSaved }) {
  const [f, setF] = useState(product ? {
    ...product,
    notes: product.notes || { top: [], heart: [], base: [] },
    images: product.images || [], volumes: product.volumes || [],
  } : EMPTY);
  const [volText, setVolText] = useState(volumesToText(f.volumes));
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setNote = (k, v) => setF((p) => ({ ...p, notes: { ...p.notes, [k]: v.split(",").map((s) => s.trim()).filter(Boolean) } }));
  const setShopCategory = (label) => {
    const m = SHOP_CATEGORIES[label];
    setF((p) => ({ ...p, category: m.category, subcategory: m.types && m.types.includes(p.subcategory) && p.category === m.category ? p.subcategory : m.subcategory }));
  };

  const save = async () => {
    if (!f.name.trim() || !f.brand.trim()) { toast.error("Name and brand are required"); return; }
    if (Number(f.price) <= 0) { toast.error("Price must be greater than 0"); return; }
    setBusy(true);
    try {
      const body = { ...f, price: Number(f.price), stock: Number(f.stock), rating: Number(f.rating),
        reviews_count: Number(f.reviews_count), volumes: textToVolumes(volText) };
      if (product) await api.put(`/admin/products/${product.id}`, body);
      else await api.post("/admin/products", body);
      toast.success(product ? "Product updated" : "Product created");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const shopCat = shopCategoryOf(f);
  const isBeauty = shopCat === "Skincare" || shopCat === "Soaps";

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="product-modal">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[#141416] gold-border rounded-xl max-h-[88vh] overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b border-[rgba(212,175,55,0.18)] sticky top-0 bg-[#141416]">
          <h3 className="font-serif-lux text-2xl text-[#FAFAF7]">{product ? "Edit Product" : "New Product"}</h3>
          <button data-testid="product-modal-close" onClick={onClose}><X className="text-[#FAFAF7]/70" /></button>
        </div>
        <div className="p-6 grid sm:grid-cols-2 gap-4">
          <input data-testid="pm-name" placeholder="Name" value={f.name} onChange={(e) => set("name", e.target.value)} className="input-lux" />
          <input data-testid="pm-brand" placeholder="Brand" value={f.brand} onChange={(e) => set("brand", e.target.value)} className="input-lux" />
          <div>
            <p className="eyebrow text-[0.55rem] mb-1.5">Category</p>
            <select data-testid="pm-category" value={shopCat} onChange={(e) => setShopCategory(e.target.value)} className="input-lux w-full">
              {Object.keys(SHOP_CATEGORIES).map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <p className="eyebrow text-[0.55rem] mb-1.5">Type</p>
            {SHOP_CATEGORIES[shopCat].types ? (
              <select data-testid="pm-subcategory" value={f.subcategory} onChange={(e) => set("subcategory", e.target.value)} className="input-lux w-full">
                {SHOP_CATEGORIES[shopCat].types.map((s) => <option key={s}>{s}</option>)}
              </select>
            ) : (
              <input data-testid="pm-subcategory" value={f.subcategory} readOnly className="input-lux w-full opacity-60" />
            )}
          </div>
          <select data-testid="pm-gender" value={f.gender} onChange={(e) => set("gender", e.target.value)} className="input-lux">
            {["Unisex", "Feminine", "Masculine"].map((g) => <option key={g}>{g}</option>)}
          </select>
          <input data-testid="pm-family" placeholder={isBeauty ? "Focus (e.g. Brightening, Clarifying, Herbal)" : "Fragrance family (e.g. Floral, Woody, Oriental)"} value={f.fragrance_family} onChange={(e) => set("fragrance_family", e.target.value)} className="input-lux" />
          <div>
            <p className="eyebrow text-[0.55rem] mb-1.5">Base price (USD)</p>
            <input data-testid="pm-price" type="number" min="0" step="0.01" placeholder="Price" value={f.price} onChange={(e) => set("price", e.target.value)} className="input-lux w-full" />
          </div>
          <div>
            <p className="eyebrow text-[0.55rem] mb-1.5">Stock</p>
            <input data-testid="pm-stock" type="number" min="0" placeholder="Stock" value={f.stock} onChange={(e) => set("stock", e.target.value)} className="input-lux w-full" />
          </div>
          <select data-testid="pm-intensity" value={f.intensity} onChange={(e) => set("intensity", e.target.value)} className="input-lux">
            <option>Subtle</option><option>Moderate</option><option>Intense</option>
          </select>
          <input data-testid="pm-rating" type="number" step="0.1" min="0" max="5" placeholder="Rating" value={f.rating} onChange={(e) => set("rating", e.target.value)} className="input-lux" />
          <textarea data-testid="pm-description" placeholder="Description" value={f.description} onChange={(e) => set("description", e.target.value)} className="input-lux sm:col-span-2 resize-none" rows={3} />
          <ImageUploader images={f.images} onChange={(imgs) => set("images", imgs)} />
          <div className="sm:col-span-2">
            <p className="eyebrow text-[0.55rem] mb-1.5">Sizes · format "size:extra price" (e.g. 50ml:0, 100ml:60)</p>
            <input data-testid="pm-volumes" placeholder="50ml:0, 100ml:60" value={volText} onChange={(e) => setVolText(e.target.value)} className="input-lux w-full" />
          </div>
          <input placeholder={isBeauty ? "Key ingredients (comma sep)" : "Top notes (comma sep)"} value={f.notes.top.join(", ")} onChange={(e) => setNote("top", e.target.value)} className="input-lux" />
          <input placeholder={isBeauty ? "Benefits (comma sep)" : "Heart notes (comma sep)"} value={f.notes.heart.join(", ")} onChange={(e) => setNote("heart", e.target.value)} className="input-lux" />
          <input placeholder={isBeauty ? "Skin type (comma sep)" : "Base notes (comma sep)"} value={f.notes.base.join(", ")} onChange={(e) => setNote("base", e.target.value)} className="input-lux sm:col-span-2" />
          <div className="flex gap-6 sm:col-span-2">
            <label className="flex items-center gap-2 text-sm text-[#FAFAF7]/70"><input data-testid="pm-bestseller" type="checkbox" checked={f.is_best_seller} onChange={(e) => set("is_best_seller", e.target.checked)} /> Best Seller</label>
            <label className="flex items-center gap-2 text-sm text-[#FAFAF7]/70"><input data-testid="pm-new" type="checkbox" checked={f.is_new_arrival} onChange={(e) => set("is_new_arrival", e.target.checked)} /> New Arrival</label>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[rgba(212,175,55,0.18)] flex justify-end gap-3 sticky bottom-0 bg-[#141416]">
          <button onClick={onClose} className="btn-outline-gold px-6 py-2.5 rounded-full text-sm">Cancel</button>
          <button data-testid="pm-save" onClick={save} disabled={busy} className="btn-gold px-8 py-2.5 rounded-full text-sm disabled:opacity-60">{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}
